import { ApiError } from '@/shared/errors';

const CHUNK_BYTES = 8 * 1024 * 1024;

/** GCS resumable URI stays in memory; never send it through API diagnostics. */
export const uploadResumableVideo = async (
  file: File, url: string, onProgress?: (percent: number) => void, signal?: AbortSignal,
) => {
  const put = (start: number, end: number, probe = false) => new Promise<{ status: number; range: string | null }>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const abort = () => xhr.abort();
    const cleanup = () => signal?.removeEventListener('abort', abort);
    const fail = (message: string) => {
      cleanup();
      reject(new ApiError(message, { status: xhr.status || undefined, request: { method: 'PUT', url: '[resumable video upload]' } }));
    };
    xhr.open('PUT', url);
    xhr.timeout = 120_000;
    xhr.setRequestHeader('Content-Type', file.type.split(';')[0] || 'application/octet-stream');
    xhr.setRequestHeader('Content-Range', probe ? `bytes */${file.size}` : `bytes ${start}-${end - 1}/${file.size}`);
    xhr.upload.addEventListener('progress', (event) => {
      if (!probe && event.lengthComputable) onProgress?.(Math.min(99, (start + event.loaded) / file.size * 100));
    });
    xhr.addEventListener('load', () => {
      cleanup();
      if (xhr.status === 308 || (xhr.status >= 200 && xhr.status < 300)) {
        resolve({ status: xhr.status, range: xhr.getResponseHeader('Range') });
      } else fail(`Video upload failed (${xhr.status}). Your upload can be retried.`);
    });
    xhr.addEventListener('error', () => fail('The upload connection was interrupted. Retry to continue.'));
    xhr.addEventListener('timeout', () => fail('The upload timed out. Retry to continue.'));
    xhr.addEventListener('abort', () => fail('Video upload cancelled.'));
    signal?.addEventListener('abort', abort, { once: true });
    if (signal?.aborted) { fail('Video upload cancelled.'); return; }
    xhr.send(probe ? null : file.slice(start, end));
  });
  const offset = (range: string | null) => range ? Number(range.match(/bytes=0-(\d+)/)?.[1]) + 1 : 0;
  let start = 0;
  let probe = true;
  let failures = 0;
  while (start < file.size) {
    try {
      const result = await put(start, Math.min(start + CHUNK_BYTES, file.size), probe);
      if (result.status !== 308) { onProgress?.(100); return; }
      const next = offset(result.range);
      if (!Number.isFinite(next) || next > file.size || (!probe && next <= start)) {
        throw new ApiError('Storage did not acknowledge this chunk. Check the bucket CORS Range header.', { status: 400, reportable: false });
      }
      if (next > start) failures = 0;
      start = next;
      probe = false;
    } catch (error) {
      if (signal?.aborted || (error instanceof ApiError && error.status && error.status < 500) || ++failures > 3) throw error;
      await new Promise((resolve) => setTimeout(resolve, 500 * 2 ** failures));
      probe = true; // Ask storage for the committed byte offset after an ambiguous failure.
    }
  }
};
